import type { PendingRow } from '@/types/inventory'

/**
 * Pure helpers behind Inventory → Pending Inventory. Kept out of the component
 * because `vitest.config.ts` only collects `src/**\/*.test.ts` — a `.tsx` test
 * would silently never run.
 */

/**
 * Longest-waiting delivery first.
 *
 * By RECEIPT, not by material: a row is one lot, and the thing worth a
 * reviewer's attention is the stock that has been sitting in quarantine
 * longest, whichever material it belongs to.
 */
export function sortPendingRowsByReceived(rows: PendingRow[]): PendingRow[] {
  return [...rows].sort(
    (a, b) => new Date(a.receivedAt).getTime() - new Date(b.receivedAt).getTime(),
  )
}

/**
 * Where a row opens, or null when there is nothing to open.
 *
 * Two routes, chosen by the data rather than by assumption: an Inventory run
 * raised from a patient's chart carries that MRN, and the patient-less page
 * omits the MRN when starting a form — which the submission API refuses for a
 * run that has one.
 *
 * Null when the run has been deleted. The lot still belongs on the list (it is
 * holding stock and nothing is clearing it) but there is no run to open, and
 * building a URL around a missing id would land on a broken page.
 */
export function pendingRunHref(row: PendingRow): string | null {
  return procedureRunHref(row.procedureId, row.patientMrn)
}

/**
 * The same rule, reachable from anywhere holding a run and its patient.
 *
 * Shared so the lot row in the materials table and the Pending Inventory tab
 * cannot disagree about where a run opens — two links to the same run landing
 * on different pages is the kind of drift that is only noticed once one of them
 * starts failing.
 */
export function procedureRunHref(
  procedureId: string | null | undefined,
  patientMrn: string | null | undefined,
): string | null {
  if (!procedureId) return null
  const procedure = encodeURIComponent(procedureId)
  return patientMrn
    ? `/patients/${encodeURIComponent(patientMrn)}/procedures/${procedure}`
    : `/mes/orders/${procedure}`
}

/**
 * How far this lot's inspection has got, as a percentage.
 *
 * Zero steps reads as 0 rather than dividing by it. A template with no forms
 * clears its lot on receipt, so such a row should not be on this list at all —
 * if one appears, showing an empty bar is the honest rendering.
 */
export function pendingProgressPct(row: PendingRow): number {
  if (row.totalForms <= 0) return 0
  return Math.round((row.completedForms / row.totalForms) * 100)
}
